import { Trans } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import {
  type LotoOtherPage,
  lotoOtherPages,
  type LotoPointKey,
} from 'features/lotoReports/lotoIsolationPoints'
import { reportHasPage } from 'features/lotoReports/lotoPageAddition'
import type { LotoReportEntry } from 'features/lotoReports/lotoReportSchemas'
import { useLotoAttachedPages } from 'features/lotoReports/useLotoAttachedPages'
import type { VisitLotoPage } from 'features/lotoReports/useLotoDocumentTab'
import type { LotoPage } from 'features/lotoReports/useLotoPage'
import type { LotoSession } from 'features/lotoReports/useLotoSession'
import { type ReactElement, useCallback, useMemo } from 'react'

import type { LotoPointRowActions } from './LotoIsolationPointRow'
import { LotoIsolationPointsTable } from './LotoIsolationPointsTable'
import { LotoPanelScroll, LotoPointsSection } from './lotoPagePanel.styles'
import { LotoReportStatusLine } from './LotoReportStatusLine'
import { LotoSaveStatusText } from './LotoSaveStatusText'
import { LotoSessionOtherPages } from './LotoSessionOtherPages'
import { LotoTargetEquipmentList } from './LotoTargetEquipmentList'

type LotoSelectedReportProps = {
  page: LotoPage
  session: LotoSession
  report: LotoReportEntry
  onOpenPage: (page: LotoOtherPage) => void
  onVisitPage: VisitLotoPage
  onFocusPoint: (key: LotoPointKey, page?: LotoOtherPage) => void
}

function SaveStatusLine({
  session,
}: Pick<LotoSelectedReportProps, 'session'>): ReactElement {
  const { hasChanges, saving, saveError, save, discard } = session
  const disabled = !hasChanges || saving
  const saveChanges = useCallback(() => void save(), [save])
  return (
    <LotoReportStatusLine
      statusText={
        <LotoSaveStatusText
          saving={saving}
          failed={saveError !== undefined}
          hasChanges={hasChanges}
        />
      }
      actions={
        <>
          <Button
            variant='outline'
            size='sm'
            disabled={disabled}
            onClick={discard}>
            <Trans>Discard</Trans>
          </Button>
          <Button size='sm' disabled={disabled} onClick={saveChanges}>
            <Trans>Save</Trans>
          </Button>
        </>
      }
      message={
        saveError && (
          <Text variant='caption' color='destructive' role='alert'>
            {saveError.message}
          </Text>
        )
      }
    />
  )
}

function PageMissingNote({ onAdd }: { onAdd: () => void }): ReactElement {
  return (
    <Box display='flex' align='center' justify='between' gap='sm'>
      <Text variant='caption' color='muted'>
        <Trans>This page isn’t in this LOTO yet.</Trans>
      </Text>
      <Button variant='outline' size='sm' onClick={onAdd}>
        <Trans>Add this page</Trans>
      </Button>
    </Box>
  )
}

export function LotoSelectedReport({
  page,
  session,
  report,
  onOpenPage,
  onVisitPage,
  onFocusPoint,
}: LotoSelectedReportProps): ReactElement {
  const { attachments, isolation_points, target_equipment } = report.data
  const { groups, isLoading } = useLotoAttachedPages(page.scope, attachments)
  const {
    addPage,
    updatePoint,
    removePoint,
    movePoint,
    pickMode,
    setPickMode,
    removeTarget,
  } = session
  const pickEquipment = useCallback(
    (picking: boolean) => setPickMode(picking ? 'equipment' : 'points'),
    [setPickMode]
  )
  const stopPicking = useCallback(() => setPickMode('points'), [setPickMode])
  const addThisPage = useCallback(
    () => addPage(page.attachment),
    [addPage, page.attachment]
  )
  const actions = useMemo<LotoPointRowActions>(
    () => ({
      onChange: updatePoint,
      onRemove: removePoint,
      onMove: movePoint,
      onOpenPage,
      onFocus: onFocusPoint,
    }),
    [movePoint, onFocusPoint, onOpenPage, removePoint, updatePoint]
  )
  const otherPages = lotoOtherPages(groups, isolation_points, page.attachment)
  return (
    <>
      <SaveStatusLine session={session} />
      {!reportHasPage(report.data, page.attachment) && (
        <PageMissingNote onAdd={addThisPage} />
      )}
      <LotoPanelScroll>
        <LotoTargetEquipmentList
          targets={target_equipment}
          picking={pickMode === 'equipment'}
          onPickingChange={pickEquipment}
          onRemove={removeTarget}
          scope={page.scope}
          onVisitPage={onVisitPage}
        />
        <LotoPointsSection onPointerDownCapture={stopPicking}>
          <LotoIsolationPointsTable
            points={isolation_points}
            currentPage={page.attachment}
            otherPages={otherPages}
            pagesLoading={isLoading}
            actions={actions}
          />
        </LotoPointsSection>
        <LotoSessionOtherPages
          pages={otherPages.filter(({ pointCount }) => pointCount === 0)}
          isLoading={isLoading}
          onOpenPage={onOpenPage}
        />
      </LotoPanelScroll>
    </>
  )
}
